/* Max Direct V3: shared, variant-aware source normalization and matching. */
(function (root) {
  'use strict';
  const CPU = ['Intel Core i3', 'Intel Core i5', 'Intel Core i7', 'Intel Core i9', 'Intel Ultra 5', 'Intel Ultra 7', 'Intel Ultra 9', 'AMD Ryzen 3', 'AMD Ryzen 5', 'AMD Ryzen 7', 'AMD Ryzen 9', 'Intel Xeon', 'Intel Celeron', 'Intel Pentium', 'Apple M Series'];
  const CONDITION = ['Brand New', 'A Grade — Very Good', 'B Grade — Good'];
  const COMPUTER_BRANDS = ['Dell', 'HP', 'Lenovo', 'Microsoft', 'Apple', 'Other'];
  const definitions = {
    laptop: { order: ['brand', 'processor', 'ram', 'storage', 'screen', 'type', 'condition', 'graphics'], positive: ['processor', 'ram', 'storage', 'screen'], ram: [4, 8, 16, 24, 32, '64+'], storage: [128, 256, 512, 1024, '2048+'], screen: ['11', '12', '13', '14', '15', '16+'], type: ['Traditional Laptops', 'Touch Laptops', '2-in-1 Laptops', 'Mobile Workstations', 'MacBook'], brand: COMPUTER_BRANDS },
    desktop: { order: ['brand', 'processor', 'ram', 'storage', 'form', 'condition', 'graphics'], positive: ['processor', 'ram', 'storage'], ram: [4, 8, 16, 24, 32, '64+'], storage: [128, 256, 512, 1024, '2048+'], form: ['Mini Desktops', 'SFF Desktops', 'Tower', 'Workstation', 'AIO'], brand: COMPUTER_BRANDS },
    monitor: { order: ['screen', 'brand', 'resolution', 'condition'], positive: ['screen', 'resolution'], screen: ['<23', '23–24', '25–27', '28+'], resolution: ['FHD', 'WUXGA', 'QUAD HD (2K–3K)', '4K'], brand: COMPUTER_BRANDS },
    workstation: { order: ['brand', 'processor', 'ram', 'storage', 'type', 'form', 'screen', 'condition', 'graphics'], positive: ['processor', 'ram', 'storage', 'screen'], ram: [4, 8, 16, 24, 32, '64+'], storage: [128, 256, 512, 1024, '2048+'], type: ['Mobile Workstations', 'Desktop Workstations'], form: ['Mini Desktops', 'SFF Desktops', 'Tower', 'AIO'], screen: ['11', '12', '13', '14', '15', '16+'], brand: COMPUTER_BRANDS },
    tablet: { order: ['brand', 'type', 'storage', 'screen', 'connectivity', 'condition'], positive: ['storage'], storage: [16, 32, 64, 128, 256, 512, '1024+'], screen: ['<10', '10–12', '>12'], type: ['iPad', 'Windows Tablet', 'Android Tablet'], connectivity: ['Wi-Fi', 'Wi-Fi + Cellular'], brand: ['Apple', 'Samsung', 'Microsoft', 'Lenovo', 'Other'] },
    phone: { order: ['brand', 'type', 'storage', 'screen', 'condition'], positive: ['storage'], storage: [32, 64, 128, 256, 512, '1024+'], screen: ['<6', '6–6.4', '6.5+'], type: ['iPhone', 'Android Phone'], brand: ['Apple', 'Samsung', 'Other'] },
    generic: { order: ['brand', 'processor', 'ram', 'storage', 'condition', 'graphics'], positive: ['processor', 'ram', 'storage'] }
  };
  function values(category, group) {
    const schema = typeof category === 'string' ? definitions[category] : category;
    return schema?.[group] || ({ processor: CPU, condition: CONDITION, graphics: ['Integrated Graphics', 'Dedicated Graphics'] })[group] || [];
  }
  const array = value => (Array.isArray(value) ? value : value == null ? [] : [value]).filter(v => v != null && String(v).trim());
  const tidy = value => String(value ?? '').normalize('NFKC').toLowerCase().replace(/[\s_–—-]+/g, ' ').trim();
  function one(value, mapper) {
    const source = array(value);
    const mapped = source.map(mapper);
    return source.length && mapped.every(Boolean) && new Set(mapped).size === 1 ? mapped[0] : null;
  }
  function positiveNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
  }
  function capacity(value) {
    const match = String(value ?? '').trim().match(/^(\d+(?:\.\d+)?)\s*(GB|G|TB)\s*(?:SSD|HDD)?$/i);
    if (!match) return null;
    const number = Number(match[1]) * (match[2].toUpperCase() === 'TB' ? 1024 : 1);
    return Number.isInteger(number) && number > 0 ? number : null;
  }
  function cpu(value) {
    const s = tidy(value);
    if (/^apple m (series)$/.test(s) || /^apple m\d+(?:\s+(?:pro|max|ultra))?$/.test(s)) return 'Apple M Series';
    let m = s.match(/^intel core (i[3579])(?:\b.*)?$/);
    if (m) return 'Intel Core ' + m[1];
    m = s.match(/^(?:intel (?:core )?)?ultra ([579])(?:\b.*)?$/);
    if (m) return 'Intel Ultra ' + m[1];
    m = s.match(/^amd ryzen ([3579])(?:\b.*)?$/);
    if (m) return 'AMD Ryzen ' + m[1];
    return /^intel xeon(?:\b.*)?$/.test(s) ? 'Intel Xeon' : /^(?:intel )?celeron\b/.test(s) ? 'Intel Celeron' : /^(?:intel )?pentium\b/.test(s) ? 'Intel Pentium' : null;
  }
  function condition(value) {
    const s = tidy(value);
    if (/\bc grade\b/.test(s)) return /\b[ab] grade\b/.test(s) ? null : 'C Grade';
    if (s === 'brand new' || s === 'new') return CONDITION[0];
    if (/\ba grade\b/.test(s) || /\bvery good\b/.test(s)) return /\bb grade\b/.test(s) ? null : CONDITION[1];
    if (/\bb grade\b/.test(s) || s === 'refurbished good' || s === 'good refurbished') return CONDITION[2];
    return null;
  }
  function graphics(value) {
    const source = array(value).map(tidy);
    if (source.some(s => s === 'dedicated' || s === 'dedicated graphics')) return 'Dedicated Graphics';
    return source.length && source.every(s => s === 'integrated' || s === 'integrated graphics') ? 'Integrated Graphics' : null;
  }
  function titleGrade(value, sku = false) {
    const source = String(value || '').normalize('NFKC');
    const found = [];
    for (const m of source.matchAll(/(?:#\s*([ABC])\b)|(?:\b([ABC])\s*[-–—]?\s*GRADE\b)|(?:\bGRADE\s*[-–—]?\s*([ABC])\b)/gi)) found.push((m[1] || m[2] || m[3]).toUpperCase());
    if (/\bbrand[\s_-]+new\b/i.test(source)) found.push('new');
    if (sku) { const m = source.match(/[-_]([ABC])$/i); if (m) found.push(m[1].toUpperCase()); }
    const unique = [...new Set(found)];
    return { explicit: unique.length > 0, conflict: unique.length > 1, value: unique.length === 1 ? ({ A: CONDITION[1], B: CONDITION[2], C: 'C Grade', new: CONDITION[0] })[unique[0]] : null };
  }
  function deviceFamily(raw) {
    const title = String(raw.title || ''), type = tidy(raw.productType);
    if (/accessor|docking|keyboard|stylus|charger|service|power adapter/i.test(type)) return 'generic';
    if (/\b(?:ipad|surface pro|surface go|x1 tablet)\b/i.test(title) || type === 'tablet' || type === 'ipad') return 'tablet';
    if (/\b(?:iphone|galaxy (?:s\d+[a-z]*|note\b|a\d+[a-z]*|z\b))\b/i.test(title) || type === 'phone' || type === 'iphone') return 'phone';
    if (/\b(?:desktop|mini pc|micro pc|all[ -]in[ -]one|imac)\b/i.test(title) || ['desktop', 'imac'].includes(type)) return 'desktop';
    if (/\b(?:laptop|macbook|notebook)\b/i.test(title) || ['laptop', 'macbook'].includes(type)) return 'laptop';
    return 'generic';
  }
  function screenSize(title) {
    const sizes = [...String(title || '').normalize('NFKC').matchAll(/\b(\d{1,2}(?:\.\d+)?)\s*[- ]?\s*(?:["″“”]|′′|inch(?:es)?\b|in\b)/gi)].map(m => Number(m[1])).filter(n => n >= 3 && n <= 100);
    return new Set(sizes).size === 1 ? sizes[0] : null;
  }
  function verifiedPhoneScreen(title) {
    const t = String(title || '');
    const model = [[/\bSM-G980F\b/i, 6.2], [/\bSM-G985F\b/i, 6.7], [/\bSM-G973F\b/i, 6.1], [/\bSM-G970F\b/i, 5.8], [/\bG781B\b/i, 6.5], [/\biPhone SE\b.*(?:3rd Gen|2022)/i, 4.7]].find(([pattern]) => pattern.test(t));
    return model ? model[1] : null;
  }
  function skuSpecs(sku, title, family) {
    const source = String(sku || '').normalize('NFKC').toUpperCase();
    const tokens = source.split(/[-_\s]+/).filter(Boolean);
    const token = tokens.find(t => /^(?:I[3579]|U[579]|ULTRA[579]|R[3579]|M\d+(?:PRO|MAX|ULTRA)?|XEON|CELERON|PENTIUM)$/.test(t));
    let processor = token ? /^I[3579]$/.test(token) ? 'Intel Core ' + token.toLowerCase() : /^(?:U|ULTRA)[579]$/.test(token) ? 'Intel Ultra ' + token.at(-1) : /^R[3579]$/.test(token) ? 'AMD Ryzen ' + token.at(-1) : /^M\d/.test(token) ? 'Apple M Series' : 'Intel ' + token[0] + token.slice(1).toLowerCase() : null;
    if (!processor && /(?:^|[-_ ])(?:[EW](?:[357])?[-_ ]\d{4}[A-Z0-9]*|(?:SILVER|GOLD|PLATINUM)[-_ ]\d{4})\b/.test(source)) processor = 'Intel Xeon';
    const parseCapacity = s => { const m = String(s || '').match(/^(\d+(?:\.\d+)?)(GB|G|TB|T)?$/); return m ? Number(m[1]) * (/^T/.test(m[2] || '') ? 1024 : 1) : null; };
    const pairs = [];
    for (let i = 0; i < tokens.length - 1; i++) {
      const ram = parseCapacity(tokens[i]);
      let storage = parseCapacity(tokens[i + 1]);
      if (storage == null && /^\d+TB\d+$/.test(tokens[i + 1])) {
        const ssds = [...String(title || '').matchAll(/(\d+(?:\.\d+)?)\s*(GB|TB)\s*SSD\b/gi)];
        if (ssds.length === 1) storage = Number(ssds[0][1]) * (ssds[0][2].toUpperCase() === 'TB' ? 1024 : 1);
      }
      if (/^\d{1,3}(?:G|GB)?$/.test(tokens[i]) && ram > 0 && ram <= 256 && storage >= 16 && storage <= 65536) pairs.push({ ram, storage });
    }
    let ram = pairs.length === 1 ? pairs[0].ram : null, storage = pairs.length === 1 ? pairs[0].storage : null;
    if (['tablet', 'phone'].includes(family) && storage == null) {
      const explicit = tokens.filter(t => /^\d+(?:GB|TB|T)$/.test(t)).map(parseCapacity);
      const plain = tokens.filter(t => /^(?:16|32|64|128|256|512|1024)$/.test(t)).map(Number);
      const candidates = explicit.length ? explicit : plain;
      if (new Set(candidates).size === 1) storage = candidates[0];
    }
    return { processor, ram, storage };
  }
  function deviceTypes(raw, family) {
    const t = String(raw.title || ''), types = [];
    if (family === 'laptop') {
      const convertible = /\b(?:2[ -]in[ -]1|x360|yoga)\b/i.test(t);
      if (!convertible) types.push('Traditional Laptops');
      if (/\btouch\b/i.test(t) && !/\b(?:non[ -]?touch|no touch)\b/i.test(t)) types.push('Touch Laptops');
      if (convertible) types.push('2-in-1 Laptops');
      if (/\bworkstation\b/i.test(t)) types.push('Mobile Workstations');
      if (/\bmacbook\b/i.test(t)) types.push('MacBook');
    }
    if (family === 'desktop' && /\bworkstation\b/i.test(t)) types.push('Desktop Workstations');
    if (family === 'tablet') {
      if (/\bipad\b/i.test(t)) types.push('iPad');
      else if (/\b(?:surface pro|surface go|x1 tablet)\b/i.test(t)) types.push('Windows Tablet');
      else if (/\b(?:android|galaxy)\b/i.test(t)) types.push('Android Tablet');
    }
    if (family === 'phone') {
      if (/\biphone\b/i.test(t)) types.push('iPhone');
      else if (/\b(?:android|galaxy)\b/i.test(t)) types.push('Android Phone');
    }
    return types;
  }
  function formFactors(raw, family) {
    if (family !== 'desktop') return [];
    const t = String(raw.title || '');
    return [[/\b(?:mini|micro|tiny)\b/i, 'Mini Desktops'], [/\b(?:sff|small form factor)\b/i, 'SFF Desktops'], [/\btower\b/i, 'Tower'], [/\b(?:aio|all[ -]in[ -]one|imac)\b/i, 'AIO'], [/\bworkstation\b/i, 'Workstation']].filter(([re]) => re.test(t)).map(([, label]) => label);
  }
  function titleResolution(title) {
    const pixels = [...String(title || '').matchAll(/\b(\d{4})\s*[x×]\s*(\d{3,4})\b/gi)].map(m => m[1] + 'x' + m[2]);
    if (pixels.length) return resolution(pixels);
    const t = String(title || '');
    if (/\b(?:4k|uhd\+?)\b/i.test(t)) return '4K';
    if (/\b(?:qhd\+?|wqhd|2k|2\.5k|3k)\b/i.test(t)) return 'QUAD HD (2K–3K)';
    if (/\bwuxga\b/i.test(t)) return 'WUXGA';
    return /\b(?:fhd|full hd)\b/i.test(t) ? 'FHD' : null;
  }
  function inventoryLabel(variant, labels) {
    if (!variant.stockKnown || !Number.isInteger(variant.inventoryQuantity)) return variant.available ? labels.not_available : labels.sold_out;
    const q = Math.max(0, variant.inventoryQuantity);
    return !q ? labels.sold_out : q > 5 ? labels.more_than_five : String(labels.available_count).replace('{count}', q);
  }
  function canonical(value, options) { return one(value, v => options.find(o => tidy(o) === tidy(v)) || null); }
  function mergeLegacyCondition(attrs) {
    const source = one(attrs.condition_source, condition);
    const legacy = one(attrs.condition_legacy, condition);
    if (array(attrs.condition_source).length > 1 && !source) return null;
    return source && legacy && source !== legacy ? null : legacy || source;
  }
  function resolution(value) {
    const known = { '1920x1080': 'FHD', 'full hd': 'FHD', fhd: 'FHD', '1920x1200': 'WUXGA', wuxga: 'WUXGA', '2560x1440': 'QUAD HD (2K–3K)', '2560x1600': 'QUAD HD (2K–3K)', '2880x1800': 'QUAD HD (2K–3K)', '3440x1440': 'QUAD HD (2K–3K)', 'quad hd': 'QUAD HD (2K–3K)', '3840x2160': '4K', '3840x2400': '4K', '4k ultra hd': '4K', '4k': '4K' };
    return one(value, v => known[tidy(v).replace(/\s*[x×]\s*/g, 'x')] || null);
  }
  function bucket(number, available) {
    if (number == null) return null;
    return available.find(v => typeof v === 'string' && v.endsWith('+') ? number >= Number(v.slice(0, -1)) : number === v) ?? number;
  }
  function screenBucket(n, category) {
    if (n == null) return null;
    if (category === 'laptop' || category === 'workstation') return n < 12 ? '11' : n >= 16 ? '16+' : String(Math.floor(n));
    if (category === 'monitor') return n < 23 ? '<23' : n < 25 ? '23–24' : n < 28 ? '25–27' : '28+';
    if (category === 'tablet') return n < 10 ? '<10' : n <= 12 ? '10–12' : '>12';
    if (category === 'phone') return n < 6 ? '<6' : n < 6.5 ? '6–6.4' : '6.5+';
    return null;
  }
  function dimension(variant, product, key, optionNames) {
    const explicit = positiveNumber(variant.attributes[key]);
    const entries = Object.entries(variant.options || {}).filter(([name]) => optionNames.includes(tidy(name)));
    const option = one(entries.map(([, v]) => v), capacity);
    if (explicit != null) return entries.length && (option == null || explicit !== option) ? null : explicit;
    return entries.length ? option : positiveNumber(product.attributes[key]);
  }
  function scopedAttribute(variant, raw, key, mapper, fallback, optionNames) {
    const explicit = variant.attributes[key];
    if (explicit != null) return mapper(explicit);
    const entries = Object.entries(variant.options || {}).filter(([name]) => optionNames.includes(tidy(name)));
    if (entries.length) return mapper(entries.map(([, v]) => v));
    if (raw.attributes[key] != null) return mapper(raw.attributes[key]);
    return fallback;
  }
  function normalizeProduct(raw, category) {
    const schema = definitions[category];
    if (!schema) throw new Error('Unmapped category');
    const a = raw.attributes || {};
    raw = { ...raw, attributes: a };
    const family = deviceFamily(raw);
    // Keep Phone/Monitor attribute matching unchanged until their dedicated QA is complete.
    const preserveAttributeMatching = category === 'phone' || category === 'monitor';
    const vendor = tidy(raw.vendor) === 'surface' ? 'microsoft' : tidy(raw.vendor);
    const brand = !vendor ? null : vendor === 'hewlett packard' || vendor === 'hp' ? 'HP' : category === 'generic' ? COMPUTER_BRANDS.find(b => tidy(b) === vendor) || String(raw.vendor).normalize('NFKC').trim().replace(/\s+/g, ' ') : schema.brand.find(b => tidy(b) === vendor) || 'Other';
    const inferredTypes = deviceTypes(raw, family), inferredForm = formFactors(raw, family);
    if (category === 'laptop' && family === 'tablet' && tidy(raw.productType) === 'laptop' && /\btouch\b/i.test(raw.title)) inferredTypes.push('Touch Laptops');
    const types = preserveAttributeMatching ? array(a.device_types).map(v => values(category, 'type').find(o => tidy(o) === tidy(v))).filter(Boolean) : [...new Set([...inferredTypes, ...array(a.device_types)])].filter(v => values(category, 'type').includes(v));
    const common = {
      brand, screen: screenBucket(preserveAttributeMatching ? positiveNumber(a.screen_inches) : screenSize(raw.title) ?? positiveNumber(a.screen_inches), category), type: types,
      form: inferredForm.length ? inferredForm.filter(v => (schema.form || []).includes(v)) : a.form_factor != null ? canonical(a.form_factor, schema.form || []) : one(a.form_source, v => ({ mini: 'Mini Desktops', 'small form factor': 'SFF Desktops', sff: 'SFF Desktops', tower: 'Tower', workstation: 'Workstation', 'all in one': 'AIO' })[tidy(v)] || null),
      resolution: (preserveAttributeMatching ? null : titleResolution(raw.title)) || resolution(a.resolution_pixels ?? a.resolution_source),
      connectivity: preserveAttributeMatching ? canonical(a.connectivity, schema.connectivity || []) : /\b(?:lte|cellular)\b/i.test(raw.title) ? 'Wi-Fi + Cellular' : /\bwi[ -]?fi\b/i.test(raw.title) ? 'Wi-Fi' : canonical(a.connectivity, schema.connectivity || [])
    };
    const variants = raw.variants.map(original => {
      const v = { ...original, attributes: original.attributes || {} }, specs = preserveAttributeMatching || family === 'phone' ? {} : skuSpecs(v.sku, raw.variants.length === 1 ? raw.title : '', family);
      const skuCondition = titleGrade(v.sku, true);
      const savedCondition = scopedAttribute(v, raw, 'condition', val => one(val, condition), one(a.condition_legacy, condition) || one(a.condition_source, condition), ['condition', 'grade']);
      const grade = savedCondition || (skuCondition.explicit ? skuCondition.value : CONDITION[1]);
      return { ...v, facets: {
      ...common,
      ram: bucket(specs.ram ?? dimension(v, raw, 'ram_gb', ['ram', 'memory', 'memory ram']), schema.ram || []),
      storage: bucket(specs.storage ?? dimension(v, raw, 'storage_gb', ['storage', 'ssd', 'hard drive', 'storage capacity']), schema.storage || []),
      processor: specs.processor || scopedAttribute(v, raw, 'processor', val => one(val, cpu), one(a.processor_source, cpu), ['processor', 'cpu']),
      condition: grade,
      graphics: graphics(a.graphics_source) || scopedAttribute(v, raw, 'graphics', graphics, /\b(?:nvidia|quadro|geforce|radeon pro|radeon rx)\b/i.test(raw.title) ? 'Dedicated Graphics' : null, ['graphics', 'graphics card'])
    } }; });
    return { ...raw, variants, category };
  }
  function collectionSchema(products) {
    const schema = { order: [], positive: [...definitions.generic.positive] };
    definitions.generic.order.forEach(group => {
      const found = new Set(products.flatMap(p => p.variants.flatMap(v => array(v.facets[group]))));
      const choices = group === 'brand' ? [...found].sort((a, b) => a.localeCompare(b)) : ['ram', 'storage'].includes(group) ? [...found].sort((a, b) => a - b) : values('generic', group).filter(value => found.has(value));
      if (choices.length) { schema.order.push(group); schema[group] = choices; }
    });
    return schema;
  }
  function categorySchema(products, category) {
    if (category === 'generic') return collectionSchema(products);
    const base = definitions[category], schema = { ...base, order: [...base.order] };
    ['ram', 'storage'].forEach(group => {
      const found = products.flatMap(p => p.variants.flatMap(v => array(v.facets[group]))).filter(v => typeof v === 'number');
      schema[group] = [...new Set([...(base[group] || []), ...found])].sort((a, b) => Number(String(a).replace('+', '')) - Number(String(b).replace('+', '')));
    });
    schema.order = schema.order.filter(group => products.some(p => p.variants.some(v => array(v.facets[group]).length)));
    return schema;
  }
  function emptyState() { return { groups: {}, stock: false, min: null, max: null, priceBand: null }; }
  function variantMatches(variant, state, excludedGroup) {
    if (state.stock && !variant.inStock) return false;
    if (excludedGroup !== 'price') {
      if (state.min != null && variant.price < state.min * 100) return false;
      if (state.max != null && variant.price > state.max * 100) return false;
      if (state.priceBand === 'under' && variant.price >= 50000) return false;
      if (state.priceBand === 'middle' && (variant.price < 50000 || variant.price > 100000)) return false;
      if (state.priceBand === 'over' && variant.price <= 100000) return false;
    }
    return Object.entries(state.groups).every(([group, selected]) => group === excludedGroup || !selected.length || selected.some(value => array(variant.facets[group]).map(String).includes(String(value))));
  }
  function matchProduct(product, state, excludedGroup, requireStock = false) {
    const matches = product.variants.filter(v => (!requireStock || v.inStock) && variantMatches(v, state, excludedGroup));
    matches.sort((a, b) => Number(b.inStock) - Number(a.inStock) || a.price - b.price || a.id - b.id);
    return matches.length ? { product, variant: matches[0], variants: matches, inStock: matches.some(v => v.inStock) } : null;
  }
  function countFacets(products, state, category, positiveStockOnly = false) {
    const schema = typeof category === 'string' ? definitions[category] : category, result = {};
    schema.order.forEach(group => {
      const counts = Object.fromEntries(values(category, group).map(v => [v, 0]));
      products.forEach(product => {
        // Display counts use exactly the same stock mode as the result list.
        // Positive-stock counts are a separate option-visibility calculation.
        const match = matchProduct(product, state, group, positiveStockOnly && schema.positive.includes(group));
        if (!match) return;
        const seen = new Set(match.variants.flatMap(v => array(v.facets[group])).map(String));
        seen.forEach(value => { if (Object.hasOwn(counts, value)) counts[value]++; });
      });
      result[group] = counts;
    });
    return result;
  }
  function buildResults(products, state, sort = 'featured', page = 1, pageSize = 12) {
    const results = products.map(p => matchProduct(p, state)).filter(Boolean);
    const compare = {
      'price-ascending': (a, b) => a.variant.price - b.variant.price,
      'price-descending': (a, b) => b.variant.price - a.variant.price,
      'title-ascending': (a, b) => a.product.title.localeCompare(b.product.title),
      'title-descending': (a, b) => b.product.title.localeCompare(a.product.title),
      'created-descending': (a, b) => b.product.createdAt - a.product.createdAt,
      featured: (a, b) => a.product.featuredRank - b.product.featuredRank
    }[sort] || ((a, b) => a.product.featuredRank - b.product.featuredRank);
    results.sort((a, b) => Number(b.inStock) - Number(a.inStock) || compare(a, b) || a.product.id - b.product.id);
    const pages = Math.max(1, Math.ceil(results.length / pageSize));
    const safePage = Math.max(1, Math.min(page, pages));
    return { total: results.length, pages, page: safePage, items: results.slice((safePage - 1) * pageSize, safePage * pageSize) };
  }
  const api = { definitions, values, normalizeProduct, collectionSchema, categorySchema, matchProduct, countFacets, buildResults, emptyState, titleGrade, skuSpecs, deviceFamily, deviceTypes, formFactors, screenSize, inventoryLabel };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.MaxDirectFilterModel = api;
})(typeof window !== 'undefined' ? window : globalThis);
